import { Card, IconEye } from "@dream/ui";
import type { ReactNode } from "react";
import { useMediaQuery } from "../hooks/use-viewport.ts";
import { useClientBrand } from "../lib/use-client-brand.ts";
import { ClientMark } from "./client-mark.tsx";
import { LegalNav } from "./cookie-consent.tsx";
import { DreamLogo } from "./dream-logo.tsx";
import { ThemeToggle } from "./theme-toggle.tsx";

/**
 * Chrome compartilhado do acompanhamento (link tokenizado e visão `/@handle`).
 *
 * A logomarca do cliente é o ícone local: aba do navegador, barra do PIN e
 * cabeçalho depois do desbloqueio. O lockup da Dream Calendar só aparece
 * quando ainda não sabemos quem é o cliente.
 *
 * Espaçamento na grade de 8px (`--dream-portal-gutter`). No telefone o hero
 * some para o calendário — agenda, mês, semana — ocupar a viewport.
 */

export function PortalFrame({
  children,
  clientName,
  clientColor,
  clientLogoUrl,
  gateName,
  eyebrow = "Acompanhamento de entregas",
  blurb,
  summary,
}: {
  children: ReactNode;
  clientName?: string;
  clientColor?: string;
  clientLogoUrl?: string | null;
  /** Nome no PIN, sem o hero nem o selo de somente leitura. */
  gateName?: string;
  eyebrow?: string;
  blurb?: string;
  summary?: string;
}) {
  const accent = clientColor ?? "#0074FF";
  const unlocked = clientName !== undefined;
  const markName = clientName ?? gateName;
  const phone = useMediaQuery("(max-width: 639px)");

  useClientBrand({
    name: markName,
    color: clientColor,
    logoUrl: clientLogoUrl,
  });

  return (
    <div
      className="dream-portal flex h-full min-h-0 flex-col overflow-x-clip bg-[var(--color-surface-subtle)]"
      data-unlocked={unlocked ? "true" : undefined}
    >
      <a href="#portal-conteudo" className="dream-skip-link">
        Ir para o calendário
      </a>
      <header className="shrink-0 border-b border-[var(--color-line)] bg-[var(--color-surface)] pt-[env(safe-area-inset-top)]">
        <div className="h-1.5 w-full" style={{ backgroundColor: accent }} aria-hidden="true" />
        <div className="dream-portal__bar flex items-center gap-2 sm:gap-3">
          {markName !== undefined ? (
            <div
              className="flex min-w-0 items-center gap-2 sm:gap-2.5"
              data-testid="portal-header-brand"
            >
              <ClientMark
                alt={unlocked ? (clientName ?? "") : ""}
                color={accent}
                logoUrl={clientLogoUrl}
                name={markName}
                size={phone ? 32 : 40}
              />
              {!unlocked ? (
                <span className="truncate text-[0.9375rem] font-bold tracking-[0] text-[var(--color-ink)]">
                  {markName}
                </span>
              ) : (
                <h1 className="truncate text-[0.9375rem] font-bold tracking-[0] text-[var(--color-ink)] sm:hidden">
                  {clientName}
                </h1>
              )}
            </div>
          ) : (
            <DreamLogo />
          )}
          <div className="ml-auto flex shrink-0 items-center gap-1.5 sm:gap-2">
            {unlocked && (
              <span
                data-testid="portal-read-only"
                className="inline-flex h-7 items-center gap-1.5 rounded-full border border-[var(--color-line)] bg-[var(--color-surface-muted)] px-2 text-[0.6875rem] font-semibold text-[var(--color-ink-secondary)] sm:px-2.5"
              >
                <IconEye className="size-3" aria-hidden="true" />
                Somente leitura
              </span>
            )}
            <ThemeToggle compact />
          </div>
        </div>
        {unlocked && clientName !== undefined && summary !== undefined && summary !== "" ? (
          <p className="dream-portal__bar pb-2 pt-0 text-[0.75rem] font-medium leading-5 text-[var(--color-ink-secondary)] sm:hidden">
            {summary}
          </p>
        ) : null}
        {unlocked && clientName !== undefined ? (
          <div className="dream-portal-hero dream-portal__hero hidden pt-1 sm:block">
            <p className="text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-[var(--color-ink-muted)]">
              {eyebrow}
            </p>
            <div className="mt-2 flex items-start gap-3 lg:gap-4">
              <ClientMark
                alt=""
                color={accent}
                logoUrl={clientLogoUrl}
                name={clientName}
                size={48}
              />
              <div className="min-w-0 flex-1">
                <h1 className="text-[1.25rem] font-bold leading-tight tracking-[0] text-[var(--color-ink)] lg:text-[1.5rem]">
                  {clientName}
                </h1>
                <p className="mt-1 hidden max-w-2xl text-[0.875rem] leading-5 text-[var(--color-ink-secondary)] md:block">
                  {blurb ??
                    "Tudo o que a Dream Vision está produzindo para você. Toque numa entrega para ver tipo, data e status."}
                </p>
                {summary !== undefined && summary !== "" ? (
                  <p className="mt-1 text-[0.8125rem] font-medium leading-5 text-[var(--color-ink)] lg:mt-2">
                    {summary}
                  </p>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}
      </header>
      <main id="portal-conteudo" data-app-main className="flex min-h-0 min-w-0 flex-1 flex-col">
        {children}
      </main>
      <footer
        className={[
          "dream-portal__foot shrink-0 border-t border-[var(--color-line)] bg-[var(--color-surface)] py-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] sm:py-3 sm:pb-[max(0.75rem,env(safe-area-inset-bottom))]",
          unlocked ? "hidden sm:block" : "",
        ].join(" ")}
      >
        <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-1.5">
          <p className="text-[0.75rem] leading-5 text-[var(--color-ink-muted)]">
            Dream Vision · sua agenda de entregas
          </p>
          <LegalNav />
        </div>
      </footer>
    </div>
  );
}

export function PortalMessage({
  title,
  message,
  action,
}: {
  title: string;
  message: string;
  action?: ReactNode;
}) {
  return (
    <div className="dream-enter flex flex-1 items-center justify-center px-4 py-10 sm:py-12">
      <Card className="w-full max-w-md p-5 text-center sm:p-6">
        <h1 className="text-base font-bold tracking-[0] text-[var(--color-ink)]">{title}</h1>
        <p className="mt-2 text-[0.8125rem] leading-5 text-[var(--color-ink-muted)]">{message}</p>
        {action !== undefined && <div className="mt-4 flex justify-center">{action}</div>}
      </Card>
    </div>
  );
}
